import { Component, OnInit, Inject, Output, HostListener, EventEmitter } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { RoomService } from 'src/app/core/services/configuration/room.service';
import { CompanyService } from 'src/app/core/services/configuration/company.service';
import { PremiseService } from 'src/app/core/services/configuration/premise.service';
import { ISessionLocal, ISessionPremise, SessionService } from 'src/app/core/services/session/session.service';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { getActiveCompanyId, getActivePremiseId } from 'src/app/entities/constants';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss']
})
export class NavbarComponent implements OnInit {
  Usuario: any;
  idRole: string;
  currentTime = new Date();
  currentPageTitle: string = 'Dashboard';
  isDark: boolean = false;
  userInitial: string = '';
  roleLabel: string = '';
  profileMenuOpen = false;

  // ─── Local Switcher ──────────────────────
  lsOpen = false;
  locals: ISessionLocal[] = [];
  activeLocalId: string = '';
  activeLocalName: string = '';
  companyName: string = '';
  idCompany = getActiveCompanyId();
  idPremise = getActivePremiseId();

  // ─── Premise Switcher (multi-local) ─────
  premises: ISessionPremise[] = [];
  activePremiseId: number = 0;
  activePremiseName: string = '';
  isSysa: boolean = false;

  @Output() toggleSidebarEvent = new EventEmitter<void>();


  constructor(
    @Inject(DOCUMENT) private document: Document,
    private router: Router,
    public themeService: ThemeService,
    private sessionService: SessionService,
    private roomService: RoomService,
    private companyService: CompanyService,
    private premiseService: PremiseService
  ) {
    router.events.forEach((event) => {
      if (event instanceof NavigationEnd) {
        this.updatePageTitle(event.urlAfterRedirects || event.url);
        this.profileMenuOpen = false;
        this.lsOpen = false;
      }
    });
  }

  ngOnInit(): void {
    this.idRole = sessionStorage.getItem('idRole') || '';
    this.isSysa = this.idRole === 'SYSA';
    const roleLabelMap: { [key: string]: string } = {
      'SYSA': 'Super Admin',
      'ADMI': 'Admin',
      'CAJE': 'Cajero',
      'MOZO': 'Mesero',
    };
    this.roleLabel = roleLabelMap[this.idRole] || 'Usuario';

    setInterval(() => { this.currentTime = new Date(); }, 1000);

    const name = sessionStorage.getItem('name') || '';
    const lastName = sessionStorage.getItem('lastName') || '';
    this.userInitial = name.charAt(0).toUpperCase();

    this.Usuario = {
      name: name,
      lastName: lastName,
      userName: sessionStorage.getItem('userName') || '',
    };

    this.themeService.isDark$.subscribe(dark => this.isDark = dark);

    // ─── Session: subscribe to active local ──
    this.sessionService.activeLocal$.subscribe(local => {
      if (local) {
        this.activeLocalId = local.idRoom;
        this.activeLocalName = local.description;
      }
    });

    this.sessionService.locals$.subscribe(locs => {
      this.locals = locs;
    });

    this.sessionService.companyName$.subscribe(name => {
      this.companyName = name;
    });

    // ─── Premise switcher subscriptions ─────
    this.sessionService.premises$.subscribe(premises => {
      this.premises = premises;
    });

    this.sessionService.activePremise$.subscribe(premise => {
      if (premise) {
        this.activePremiseId = premise.idPremise;
        this.activePremiseName = premise.description;
      }
    });

    // ─── Load data from APIs ──────────────────
    this.loadCompany();
    // Solo ADMI puede ver TODOS los locales de la empresa. MOZO y CAJE
    // quedan amarrados a los locales asignados por el administrador
    // (vienen en r.data.premises desde el login). Esto evita que un
    // mozo pueda cambiar de local libremente desde el switcher.
    if (this.idRole === 'ADMI') {
      this.loadPremises();
    } else if (!this.isSysa) {
      this.applyAssignedPremises();
    }
  }

  // ════════════════════════════════════════════
  //  Aplicar SOLO los premises asignados al usuario
  //  (cruzando contra la lista real del backend para
  //  obtener codePremise/address). Para roles operativos
  //  (MOZO/CAJE) — el set se filtra al que el admin le
  //  asignó, así nunca pueden ver locales ajenos.
  // ════════════════════════════════════════════
  private applyAssignedPremises(): void {
    const assigned = this.sessionService.currentPremises || [];
    if (!assigned.length) return;

    const allowedIds = new Set(assigned.map(p => Number(p.idPremise)));

    this.premiseService.List().subscribe(r => {
      let resolved: ISessionPremise[] = assigned;

      if (r && r.status === true && Array.isArray(r.data) && r.data.length > 0) {
        const activePremises = r.data.filter((p: any) => p.state == 1 || p.state == '1');
        const mapped: ISessionPremise[] = activePremises
          .filter((p: any) => allowedIds.has(parseInt(p.idPremise, 10)))
          .map((p: any) => {
            // Conservar el idRoom que vino en la sesión del login para
            // este premise (la API de premise no lo trae).
            const fromSession = assigned.find(a => Number(a.idPremise) === parseInt(p.idPremise, 10));
            return {
              idPremise: parseInt(p.idPremise, 10),
              description: p.codePremise && p.address ? `${p.codePremise} — ${p.address}` : p.codePremise || `Local ${p.idPremise}`,
              address: p.address || '',
              phone: p.phone || '',
              state: p.state ?? 1,
              ipCashbox: p.ipCashbox || '',
              idRoom: fromSession?.idRoom,
            };
          });
        if (mapped.length) resolved = mapped;
      }

      this.sessionService.setPremises(resolved);

      const currentActive = this.sessionService.currentPremise;
      const activeInList = currentActive
        ? resolved.find(p => p.idPremise === currentActive.idPremise)
        : undefined;

      if (!activeInList) {
        // El local activo ya no está permitido: caemos al primero asignado.
        this.sessionService.switchPremise(resolved[0]);
      } else if (currentActive && currentActive.description !== activeInList.description) {
        // Refrescamos la descripción si quedó con placeholder "Local N".
        this.sessionService.switchPremise(activeInList);
      }
    }, () => {
      // Si List() falla, al menos refrescamos el observable con lo del login.
      this.sessionService.setPremises(assigned);
    });
  }

  // ════════════════════════════════════════════
  //  Load company name
  // ════════════════════════════════════════════
  loadCompany(): void {
    this.companyService.GetData().subscribe(r => {
      if (typeof r === 'object' && r.data) {
        const company = Array.isArray(r.data) ? r.data[0] : r.data;
        if (company && company.businessName) {
          this.sessionService.setCompanyName(company.businessName);
        }
      }
    }, () => {
      // API no disponible: usar nombre por defecto
      if (!this.companyName) {
        this.sessionService.setCompanyName('EL FOGÓN CHICKEN GRILL S.A.C.');
      }
    });
  }

  // ════════════════════════════════════════════
  //  Load available premises from PremiseService
  // ════════════════════════════════════════════
  loadPremises(): void {
    this.premiseService.List().subscribe(r => {
      if (r && r.status === true && Array.isArray(r.data) && r.data.length > 0) {
        const activePremises = r.data.filter((p: any) => p.state == 1 || p.state == '1');
        const premises: ISessionPremise[] = activePremises.map((p: any) => ({
          idPremise: parseInt(p.idPremise, 10),
          description: p.codePremise && p.address ? `${p.codePremise} — ${p.address}` : p.codePremise || `Local ${p.idPremise}`,
          address: p.address || '',
          phone: p.phone || '',
          state: p.state ?? 1,
          ipCashbox: p.ipCashbox || '',
        }));
        this.sessionService.setPremises(premises);

        // Resolver el premise activo:
        //   1) Si no hay activo o el activo guardado no existe en la lista
        //      real (premise borrado/sin acceso), usamos el último elegido
        //      por el usuario (localStorage) o el primero de la lista.
        //   2) Si el activo SÍ existe, solo refrescamos su descripción por
        //      si quedó con placeholder ("Local N").
        const currentActive = this.sessionService.currentPremise;
        const activeInList = currentActive
          ? premises.find(p => p.idPremise === currentActive.idPremise)
          : undefined;

        if (!activeInList) {
          let fallback: ISessionPremise | undefined;
          try {
            const lastId = localStorage.getItem('fg_lastPremiseId');
            if (lastId) {
              fallback = premises.find(p => String(p.idPremise) === String(lastId));
            }
          } catch (_) { }
          fallback = fallback || premises[0];
          if (fallback) {
            this.sessionService.switchPremise(fallback);
          }
        } else if (currentActive && currentActive.description !== activeInList.description) {
          this.sessionService.switchPremise(activeInList);
        }
      }
    });
  }

  // ════════════════════════════════════════════
  //  Local Switcher
  // ════════════════════════════════════════════
  toggleLocalSwitcher(e: Event): void {
    e.stopPropagation();
    // Roles operativos con un solo local asignado no tienen nada que
    // elegir: ni abrimos el dropdown para evitar UI inerte.
    if (this.idRole !== 'ADMI' && !this.isSysa && this.premises.length <= 1) {
      this.lsOpen = false;
      return;
    }
    this.lsOpen = !this.lsOpen;
    this.profileMenuOpen = false;
  }

  onSwitchLocal(local: ISessionLocal): void {
    this.sessionService.switchLocal(local);
    this.lsOpen = false;
  }

  onSwitchPremise(premise: ISessionPremise): void {
    // Defensa: aun si alguien fuerza el click vía DOM, sólo dejamos
    // cambiar a un local que esté en la lista permitida del usuario.
    const allowed = this.premises.some(p => p.idPremise === premise.idPremise);
    if (!allowed) {
      this.lsOpen = false;
      return;
    }
    this.sessionService.switchPremise(premise);
    this.lsOpen = false;
  }

  // Close dropdowns when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    this.lsOpen = false;
    this.profileMenuOpen = false;
  }

  // ════════════════════════════════════════════
  //  Existing methods
  // ════════════════════════════════════════════
  toggleDark(): void {
    this.themeService.toggleTheme();
  }

  toggleProfileMenu(): void {
    this.profileMenuOpen = !this.profileMenuOpen;
  }

  onLogout(e: Event): void {
    e.preventDefault();
    this.sessionService.clear();
    if (!sessionStorage.getItem('isLoggedinFogon')) {
      // El idCompany ya no se propaga por URL (cliente fijo COMPANY001).
      this.router.navigate(['/auth/login']);
    }
  }

  onToggleSidebar(): void {
    this.toggleSidebarEvent.emit();
  }

  private updatePageTitle(url: string): void {
    const map: { [key: string]: string } = {
      '/dashboard': 'Dashboard',
      '/location': 'Local',
      '/order': 'Pedidos',
      '/expense': 'Gastos',
      '/cashbox': 'Caja',
      '/client': 'Clientes',
      '/product': 'Productos',
      '/user': 'Usuarios',
      '/configuration': 'Configuraciones',
    };
    for (const key of Object.keys(map)) {
      if (url.startsWith(key)) {
        this.currentPageTitle = map[key];
        return;
      }
    }
    this.currentPageTitle = 'Dashboard';
  }
}
